import { useState } from "react";
import { api, SIZES, koliPairs, koliText } from "@/lib/api";
import { useData } from "@/lib/data";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import SizeMatrix from "@/components/SizeMatrix";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Package, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function KoliTypes() {
  const { koliTypes, refreshKoliTypes } = useData();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [ratios, setRatios] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setEditingId(null); setName(""); setRatios({}); };
  const startEdit = (k) => { setEditingId(k.id); setName(k.name); setRatios(k.ratios); setOpen(true); };

  const submit = async () => {
    const clean = Object.fromEntries(SIZES.map((s) => [s, parseInt(ratios[s]) || 0]).filter(([, q]) => q > 0));
    if (!name.trim()) { toast.error("Koli adı zorunlu"); return; }
    if (!Object.keys(clean).length) { toast.error("En az bir numara için adet girin"); return; }
    setSubmitting(true);
    try {
      if (editingId) await api.put(`/koli-types/${editingId}`, { name, ratios: clean });
      else await api.post("/koli-types", { name, ratios: clean });
      toast.success(editingId ? "Koli tipi güncellendi" : "Koli tipi oluşturuldu");
      setOpen(false); reset(); refreshKoliTypes();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
    finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Koli tipini silmek istiyor musunuz?")) return;
    try { await api.delete(`/koli-types/${id}`); toast.success("Silindi"); refreshKoliTypes(); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };

  const total = SIZES.reduce((a, s) => a + (parseInt(ratios[s]) || 0), 0);

  return (
    <div className="p-5 lg:p-10 max-w-[1400px]" data-testid="koli-types-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-widest text-[#B45309] mb-2">Paketleme</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight flex items-center gap-3">
            <Package className="w-7 h-7 text-[#D97706]" /> Koli Tipleri
          </h1>
          <p className="text-[#6B5B4A] mt-2 text-sm">Her koli tipinin numara dağılımını tanımlayın. Sipariş, sevkiyat ve stokta kullanılır.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
          <DialogTrigger asChild>
            <Button data-testid="koli-type-new-btn" className="bg-[#D97706] hover:bg-[#B45309] text-white font-display uppercase tracking-wide h-11 px-5">
              <Plus className="w-4 h-4 mr-2" /> Yeni Koli Tipi
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#FFFBF4] border-[#DCCFB8] text-[#2B2118] max-w-3xl" data-testid="koli-type-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl uppercase tracking-tight">{editingId ? "Koli Tipini Düzenle" : "Yeni Koli Tipi"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-[#6B5B4A]">Koli Adı *</Label>
                <Input data-testid="koli-type-name" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Örn: Erkek 12'li" className="mt-1 h-11 bg-[#EFE6D6] border-[#DCCFB8]" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#6B5B4A] mb-2 block">Koli içindeki çift sayısı (numara bazında)</Label>
                <SizeMatrix values={ratios} onChange={(s, v) => setRatios((r) => ({ ...r, [s]: v }))} testIdPrefix="koli-type-size" />
              </div>
            </div>
            <DialogFooter>
              <div className="flex items-center justify-between w-full">
                <p className="font-mono text-xl font-bold text-[#B45309]" data-testid="koli-type-total">1 koli = {total} çift</p>
                <Button onClick={submit} disabled={submitting} data-testid="koli-type-submit-btn"
                  className="bg-[#D97706] hover:bg-[#B45309] text-white font-display uppercase tracking-wide h-11 px-6">
                  {submitting ? "Kaydediliyor..." : "Kaydet"}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="koli-types-list">
        {koliTypes.map((k) => (
          <div key={k.id} className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-5" data-testid={`koli-type-card-${k.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-display text-xl font-bold uppercase tracking-tight">{k.name}</h3>
                <p className="font-mono text-[#B45309] text-sm">{koliPairs(k.ratios)} çift / koli</p>
              </div>
              <div className="flex gap-3">
                <button onClick={() => startEdit(k)} data-testid={`koli-type-edit-${k.id}`} className="text-[#8C7B68] hover:text-[#B45309]"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(k.id)} data-testid={`koli-type-delete-${k.id}`} className="text-[#8C7B68] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <p className="text-xs font-mono text-[#6B5B4A]">{koliText(k.ratios)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
